import React from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { ButtonBase, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** Text-button field that toggles the picker instead of rendering an input. */
function ButtonField(props) {
  const {
    setOpen,
    label,
    id,
    disabled,
    InputProps: { ref } = {},
    inputProps: { "aria-label": ariaLabel } = {},
    mode,
    showExpandIcon,
  } = props;

  return (
    <ButtonBase
      ref={ref}
      id={id}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => setOpen?.((prev) => !prev)}
      sx={{
        borderRadius: 1,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: mode === "add" ? "text.secondary" : "text.primary",
          fontWeight: 500,
        }}
      >
        {label || "Undated"}
      </Typography>
      {showExpandIcon && (
        <ExpandMoreIcon
          fontSize="small"
          sx={{ ml: 0.25, color: "text.secondary" }}
        />
      )}
    </ButtonBase>
  );
}

// Open state is owned by the caller so the picker can also be opened from
// outside the button itself (e.g. a menu action), not just by tapping the date.
function ButtonDatePicker({ open, setOpen, allowClear, showExpandIcon, ...props }) {
  return (
    <DatePicker
      slots={{ ...props.slots, field: ButtonField }}
      disableFuture
      slotProps={{
        ...props.slotProps,
        field: {
          setOpen,
          mode: props.mode,
          statusAction: props.statusAction,
          showExpandIcon,
        },
        actionBar: {
          actions: allowClear ? ["today", "clear", "accept"] : ["today", "accept"],
        },
      }}
      localeText={{ clearButtonLabel: "No date" }}
      {...props}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    />
  );
}

/**
 * Date shown as tappable text that opens a date picker (future dates disabled).
 *
 * minDate: optional earliest selectable date (e.g. Jan 1 for "finished this year").
 * allowClear: whether the picker offers "No date" — off where a date is required.
 * showExpandIcon: adds a chevron after the date, for places where it otherwise
 *   doesn't read as editable (off by default — the journal's dates don't use it).
 */
export default function PickerWithButtonField({
  value,
  setValue,
  mode,
  disabled,
  statusAction,
  open,
  setOpen,
  minDate,
  allowClear = true,
  showExpandIcon = false,
}) {
  const formattedValue = value ? dayjs(value) : null;
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      {/* Todo: update locale based on user preferences!!! */}
      <ButtonDatePicker
        label={
          formattedValue == null ? null : formattedValue.format("DD MMM YYYY")
        }
        value={formattedValue}
        onChange={setValue}
        mode={mode}
        disabled={disabled}
        statusAction={statusAction}
        open={open}
        setOpen={setOpen}
        allowClear={allowClear}
        showExpandIcon={showExpandIcon}
        {...(minDate && { minDate: dayjs(minDate) })}
      />
    </LocalizationProvider>
  );
}
